import { StrictMode, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, RouterProvider, createBrowserRouter, useParams } from 'react-router';
import './i18n';
import '@vatman/ui/styles.css';
import './styles/app.css';
import { CenterScreen, Spinner, Toasts } from '@vatman/ui';
import { AppUIProvider } from './ui/AppUIProvider';
import { Analytics } from './lib/analytics';
import { AuthPage } from './pages/auth/AuthPage';
import { ConsentGate } from './pages/auth/Legal';
import { ConnectPage } from './pages/auth/ConnectPage';
import { UpdateBanner } from './ui/UpdateBanner';
import { TokenPage } from './pages/auth/TokenPage';
import { InvitePage } from './pages/auth/InvitePage';
import { Dashboard } from './pages/dashboard/Dashboard';
import { AppShell } from './pages/AppShell';

const fallback = (
  <CenterScreen>
    <Spinner />
  </CenterScreen>
);

/*
 * Pages in their own chunks, loaded as route `lazy`: the router fetches the chunk before it
 * switches pages, so the current page (sidebar included) stays on screen meanwhile instead of a
 * full-screen spinner. The board pulls in the GPU renderer, so it loads only when opened; the
 * lighter pages are fetched in the background once the first page is up.
 */
const pages = {
  board: () => import('./board/BoardPage').then((m) => m.default),
  inbox: () => import('./pages/inbox/InboxPage').then((m) => m.InboxPage),
  tracker: () => import('./pages/tracker/TrackerPage').then((m) => m.TrackerPage),
  space: () => import('./pages/spaces/SpacePage').then((m) => m.SpacePage),
  page: () => import('./docs/PageView').then((m) => m.PageRoute),
  team: () => import('./pages/team/TeamPage').then((m) => m.TeamPage),
  legacyPage: () => import('./docs/Legacy').then((m) => m.LegacyPage),
  // The design system's gallery and the style snapshot (dev/): only in the dev server.
  gallery: import.meta.env.DEV ? () => import('./dev/GalleryPage').then((m) => m.default) : null,
};
const page = (load: () => Promise<ComponentType>) => ({
  lazy: async () => ({ Component: await load() }),
  // Shown only when the app is opened right on such a page.
  hydrateFallbackElement: fallback,
});
const prefetch = () => void Promise.all([pages.tracker(), pages.inbox(), pages.space()]).catch(() => {});
if ('requestIdleCallback' in window) requestIdleCallback(prefetch, { timeout: 4000 });
else setTimeout(prefetch, 2000);
if (import.meta.env.DEV) void import('./dev/styleSnapshot').then((m) => m.install());

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});


/**
 * Old links of Jira (`/browse/PAY-123`) and YouTrack (`/issue/MOB-3/title`): when the old
 * address is pointed at Vatman after a move, they open the same issue here — keys are kept.
 */
function LegacyIssue() {
  const { key = '' } = useParams();
  return <Navigate to={`/t/i/${encodeURIComponent(key.toUpperCase())}`} replace />;
}

const legacy = ['/browse/:key', '/jira/browse/:key', '/projects/:project/issues/:key', '/issue/:key/*', '/youtrack/issue/:key/*'].map((path) => ({ path, element: <LegacyIssue /> }));
/** Old Confluence links (docs/Legacy.tsx): Cloud's under /wiki, Data Center's under any path. */
const legacyPages = ['/display/*', '/pages/viewpage.action', '/spaces/:key/pages/*', '/x/:code', '/wiki/*', '/confluence/*'].map((path) => ({ path, ...page(pages.legacyPage) }));

const router = createBrowserRouter([
  {
    // The pages with the menu share one frame (pages/AppShell.tsx): the menu is made once.
    element: <AppShell />,
    children: [
      { path: '/', element: <Dashboard /> },
      ...['/t', '/t/:projectId', '/t/i/:issueKey', '/t/f/:filterId'].map((path) => ({ path, ...page(pages.tracker) })),
      { path: '/s/:spaceId', ...page(pages.space) },
      { path: '/d/:pageId', ...page(pages.page) },
      { path: '/inbox', ...page(pages.inbox) },
      { path: '/team/:teamId', ...page(pages.team) },
      { path: '*', element: <Dashboard /> },
    ],
  },
  { path: '/login', element: <AuthPage mode="login" /> },
  { path: '/register', element: <AuthPage mode="register" /> },
  { path: '/forgot', element: <AuthPage mode="forgot" /> },
  { path: '/verify', element: <TokenPage kind="verify" /> },
  { path: '/magic', element: <TokenPage kind="magic" /> },
  { path: '/reset', element: <TokenPage kind="reset" /> },
  { path: '/connect', element: <ConnectPage /> },
  { path: '/invite/:token', element: <InvitePage kind="board" /> },
  { path: '/invite/team/:token', element: <InvitePage kind="team" /> },
  { path: '/invite/org/:token', element: <InvitePage kind="org" /> },
  { path: '/invite/project/:token', element: <InvitePage kind="project" /> },
  { path: '/b/:id', ...page(pages.board) },
  ...legacy,
  ...legacyPages,
  ...(pages.gallery ? [{ path: '/dev/ui', ...page(pages.gallery) }] : []),
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppUIProvider>
        <RouterProvider router={router} />
        <Toasts />
        <ConsentGate />
        <UpdateBanner />
        <Analytics />
      </AppUIProvider>
    </QueryClientProvider>
  </StrictMode>,
);
